import React, {useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import styled from 'styled-components';
import AccountDetailsForm from '../components/AccountDetailsForm';
import SignupHeader from '../components/SignupHeader';
import Spinner from '../components/Spinner';
import SubscriptionDetailsForm from '../components/SubscriptionDetailsForm';
import {Icon} from '@blueprintjs/core';
import fetcher from '../modules/fetcher';
import notifications from '../modules/notifications';
import {colors, media, radius, shadowTight} from '../modules/styles';

export default function SignupAccountInfoPg({match}) {
  const [account, setAccount] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const loadData = async token => {
      try {
        const res = await fetcher.get(`/api/accounts/token/${token}`);
        setAccount(res.data);
      } catch (e) {
        setIsExpired(true);
      }
    };

    if (match.params.code) {
      loadData(match.params.code);
    }
  }, [match.params.code]);

  const onChange = data => {
    setFormData(prevFormData => ({...prevFormData, ...data}));
  };

  const validateForm = () => {
    if (!formData) return false;
    let isValid = true;
    const errors = {};

    [
      'name',
      'phone',
      'address1',
      'city',
      'stateProv',
      'country',
      'zipPostal',
    ].forEach(field => {
      if (!formData[field] && !account[field]) {
        errors[field] = true;
        isValid = false;
      }
    });

    setFormData({...formData, errors});
    return isValid;
  };

  const onSubmit = async e => {
    try {
      e.preventDefault();
      const isValid = validateForm();

      if (!isValid) {
        notifications.error('Please check the form for errors and try again.');
        return;
      }

      const res = await fetcher.patch(
        `/api/accounts/token/${match.params.code}`,
        formData
      );
      setAccount(res.data);
      setFormData(null);
      notifications.success(`Account Updated Successfully!`);
    } catch (e) {
      notifications.error(`Error Updating Account: ${e?.data}`);
    }
  };

  return (
    <Comp>
      <Helmet title="Account Info" />
      <SignupHeader disabled={true} showSteps={false} />

      {isExpired ? (
        <Row>
          <ExpiredBox>
            <Icon icon="time" size={36} color={colors.secondary} />
            <h2>This link has expired</h2>
            <p>
              Please contact your Compliance Works rep to generate a new one.
            </p>
          </ExpiredBox>
        </Row>
      ) : account ? (
        <>
          <Row>
            <Box>
              <h2>Your Subscription Details:</h2>
              <SubscriptionDetailsForm account={account} onChange={onChange} />
            </Box>
          </Row>

          <Row>
            <Box>
              <h2>Your Account Details:</h2>
              <AccountDetailsForm account={account} onChange={onChange} />
              <Buttons>
                <NextButton disabled={!formData} onClick={onSubmit}>
                  Save Account Info
                </NextButton>
              </Buttons>
            </Box>
          </Row>
        </>
      ) : (
        <Row>
          <Box>
            <Spinner />
          </Box>
        </Row>
      )}
    </Comp>
  );
}

const Comp = styled.div`
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  margin: 20px 0;
  width: 100%;

  @media (min-width: ${media.md}) {
    justify-content: center;
  }
`;

const ExpiredBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 50px 40px;
  margin: 40px auto;
  width: 100%;
  max-width: 440px;
  box-shadow: ${shadowTight};
  border-radius: ${radius};
  background-color: white;
  border-top: 4px solid ${colors.secondary};

  h2 {
    margin: 16px 0 8px 0;
    color: ${colors.secondary};
  }

  p {
    margin: 0;
    color: ${colors.gray};
  }
`;

const Box = styled.div`
  padding: 20px;
  margin: 0 0 20px 0;
  height: fit-content;

  box-shadow: ${shadowTight};
  border-radius: ${radius};
  background-color: white;
  border-top: 4px solid ${colors.secondary};

  h2 {
    margin: 0 0 20px 0;
  }

  @media (min-width: ${media.md}) {
    margin: 0px 0px 20px 0;
    width: 50%;

    &:first-of-type {
      margin-right: 20px;
    }
  }
`;

const Buttons = styled.div`
  display: flex;
  margin: 30px 0 30px 0;
  justify-content: center;
`;

const NextButton = styled.button`
  padding: 15px 20px;
  border: none;
  background-color: ${colors.primary2};
  border-radius: ${radius};
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease-out;

  &:hover {
    transform: scale(1.03);
    transition: all 150ms ease-out;
  }

  &:disabled {
    background-color: ${colors.gray2};
    cursor: not-allowed;
  }
`;
